// src/pages/business/CMO.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  FlaskConical,
  Package,
  Layers,
  Repeat,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";

export default function CMO() {
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.25 }, // same as API.jsx
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const services = [
    {
      title: "Process & Route Development",
      text: "Efficient, scalable pathways designed to reduce steps, waste, and cost.",
      icon: Layers,
    },
    {
      title: "Technology Transfer (In/Out)",
      text: "Structured knowledge capture, smooth handovers, and rapid plant readiness.",
      icon: Repeat,
    },
    {
      title: "Scale-Up & Manufacturing",
      text: "Kilo lab → pilot → commercial campaigns with the same controls at every stage.",
      icon: FlaskConical,
    },
    {
      title: "Custom Synthesis",
      text: "Targeted work across APIs, advanced intermediates, and specialty chemistries.",
      icon: ClipboardCheck,
    },
    {
      title: "Analytical Support",
      text: "Method development, verification, in-process controls, and release testing.",
      icon: ShieldCheck,
    },
    {
      title: "Packaging & Dispatch",
      text: "Fit-for-purpose packs, clear labelling, and complete documentation.",
      icon: Package,
    },
  ];

  const quality = [
    "Built-In Quality: Stage-gate reviews, risk assessments, and “right-first-time” planning for every campaign.",
    "Robust Controls: Change control, deviation handling, and corrective/preventive actions with full traceability.",
    "Clean Manufacturing: Segregated areas, controlled material flow, and validated cleaning procedures to prevent cross-contamination.",
    "Data Confidence: Secure, auditable records; clear batch documentation and Certificates of Analysis.",
    "People & Training: Competency-based training, shop-floor coaching, and routine refreshers.",
    "Safe Operations: Formal hazard reviews, safe-work instructions, and continuous monitoring.",
    "Environment & Responsibility: Thoughtful solvent selection, recovery where feasible, and waste minimization.",
  ];

  const whyMacLabs = [
    "Quality without compromise from lab to final release.",
    "Safety first in people, process, and plant.",
    "Speed with discipline—fast, but never at the expense of control.",
    "Partnership mindset—flexible, responsive, and easy to work with.",
  ];

  return (
    <div className="w-full">
      {/* ✅ Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/cmo.webp')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Contract Manufacturing (CMO/CDMO)
        </h1>
      </div>

      {/* ✅ Content Section */}
      <motion.div
        className="max-w-6xl mx-auto px-6 py-16"
        initial="hidden"
        animate="visible"   // ✅ plays immediately on load
        variants={container}
      >
        {/* Intro */}
        <motion.p
          className="text-lg text-gray-700 mb-6 text-center max-w-4xl mx-auto"
          variants={fadeInUp}
        >
          End-to-end development and manufacturing—built on quality, safety, and
          reliability.
        </motion.p>
        <motion.p
          className="text-gray-600 mb-10 text-center max-w-4xl mx-auto"
          variants={fadeInUp}
        >
          MAC LABS LTD partners with innovators and generics companies to take
          molecules from idea to steady, large-scale supply. We blend deep
          process chemistry with disciplined execution so every batch is
          consistent, documented, and on time.
        </motion.p>

        {/* Services Section */}
        <motion.h2
          className="text-2xl font-semibold text-[#0d47a1] mb-6 relative"
          variants={fadeInUp}
        >
          What We Do
          <span className="absolute bottom-0 left-0 w-16 h-[3px] bg-[#0d47a1] rounded-full"></span>
        </motion.h2>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
          variants={fadeInUp}
        >
          {services.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                className="group bg-white shadow-md rounded-2xl p-6 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transform transition duration-300"
                variants={fadeInUp}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0d47a1] text-white mr-4 transition-transform duration-300 group-hover:scale-110">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600">{item.text}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Quality & Safety Section */}
        <motion.h2
          className="text-2xl font-semibold text-[#0d47a1] mb-6 relative"
          variants={fadeInUp}
        >
          Quality & Safety—At the Core
          <span className="absolute bottom-0 left-0 w-16 h-[3px] bg-[#0d47a1] rounded-full"></span>
        </motion.h2>
        <motion.ul
          className="space-y-3 text-gray-700 mb-16"
          variants={fadeInUp}
        >
          {quality.map((q, idx) => (
            <motion.li
              key={idx}
              className="flex items-start gap-3"
              variants={fadeInUp}
            >
              <ShieldCheck className="w-5 h-5 text-[#0d47a1] mt-1 flex-shrink-0" />
              <span>{q}</span>
            </motion.li>
          ))}
        </motion.ul>

        {/* Why MAC LABS Section */}
        <motion.h2
          className="text-2xl font-semibold text-[#0d47a1] mb-6 relative"
          variants={fadeInUp}
        >
          Why MAC LABS LTD
          <span className="absolute bottom-0 left-0 w-16 h-[3px] bg-[#0d47a1] rounded-full"></span>
        </motion.h2>
        <motion.ul className="space-y-3 text-gray-700" variants={fadeInUp}>
          {whyMacLabs.map((reason, idx) => (
            <motion.li
              key={idx}
              className="flex items-start gap-3"
              variants={fadeInUp}
            >
              <ShieldCheck className="w-5 h-5 text-[#0d47a1] mt-1 flex-shrink-0" />
              <span>{reason}</span>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </div>
  );
}
